import {
  type BankAccountDetail,
  BankAccountType,
  type InvestmentAccountDetail,
  type UseQueryResult,
  useWealthOverview,
  type WealthOverview,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { useEffect, useMemo } from 'react'
import { type TransferAccounts } from '../types'

interface TUserAccountBasedBalance {
  alias: number | null | undefined
  accountType: TransferAccounts | undefined
  onSettledCb: (
    _data:
      | {
          __typename?: 'Money'
          amount: number
          currencyCode: string
        }
      | undefined,
  ) => void
}

export const useAccountBasedBalance = ({
  alias,
  accountType,
  onSettledCb,
}: TUserAccountBasedBalance): UseQueryResult<WealthOverview, GQLError> => {
  const accountPresentToFetch = useMemo(
    () => Boolean(typeof alias === 'number' && alias !== -1 && accountType),
    [alias, accountType],
  )

  const api = useWealthOverview({
    enabled: Boolean(accountPresentToFetch),
  })

  useEffect(() => {
    if (accountPresentToFetch && api.data) {
      const accounts = [
        ...(api.data.highYieldChecking?.accounts ?? []),
        ...(api.data.highYieldSavings?.accounts ?? []),
        ...(api.data.personalPortfolio?.accounts ?? []),
      ]
      const account = accounts.find(
        (currentAccount: BankAccountDetail | InvestmentAccountDetail) => currentAccount.alias === alias,
      )

      if (!account) return

      if (accountType === BankAccountType.HIGH_YIELD_CHECKING || accountType === BankAccountType.HIGH_YIELD_SAVINGS) {
        onSettledCb((account as unknown as BankAccountDetail).balance.available)
        return
      }

      onSettledCb((account as unknown as InvestmentAccountDetail).balance.availableForWithdrawal)
    }
  }, [accountPresentToFetch, accountType, alias, api.data, onSettledCb])

  return api
}
